import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Alert, Empty, Radio, Typography } from 'antd';

const { Text } = Typography;

/**
 * Preview pieces, rendered from the SERVER's derived output.
 *
 * The fidelity guarantee is not that this matches calyxglobal.com pixel for pixel. It is
 * that both this and the website read the same `derived` object, so every number, axis
 * label, "(QTD)" suffix, arrow direction and caption shown here is the one that will
 * ship. Nothing is recomputed locally.
 *
 * Exported in pieces so each one can sit directly under the fields that feed it, rather
 * than in a separate tab where the editor has to remember what they changed.
 */

const GREEN = '#177A1B';
const INK = '#171A1C';
const PURPLE = '#A749D3';
const GREY = '#636B74';

/*
  Declared once, not per render.

  echarts-for-react skips redrawing when the new option deep-equals the old one, and
  fast-deep-equal cannot compare two functions: it never enters its object branch for
  them, so two freshly built closures are always unequal. Inline, these made the price
  chart fail that check on every recheck, and because the chart is rendered with
  `notMerge` a failed check is a full clear and a replayed entry animation. The integrity
  chart never had the problem, its option being pure data, which is why only this one
  visibly restarted while someone was typing.
*/
const priceTooltip = (v: number) => `$${Number(v).toFixed(2)}`;
const priceAxisLabel = (v: number) => `$${v}`;

/** Draws the final segment dashed when the last period is still open. */
function provisionalMarkLine(values: number[], provisional: boolean) {
  if (!provisional || !values || values.length < 2) return undefined;
  return {
    symbol: 'none',
    lineStyle: { type: 'dashed', color: '#fff', width: 2 },
    data: [[
      { coord: [values.length - 2, values[values.length - 2]] },
      { coord: [values.length - 1, values[values.length - 1]] },
    ]],
    z: 10,
  };
}

/**
 * Shown until a starting point is chosen.
 *
 * A bordered placeholder rather than nothing at all, so the section keeps its shape and
 * an empty preview reads as "not loaded" instead of "broken".
 */
const NothingYet: React.FC<{ what: string }> = ({ what }) => (
  <div
    style={{
      border: '1px dashed #d9d9d9',
      borderRadius: 2,
      padding: 24,
      background: '#fafafa',
    }}
  >
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <span>
          <Text type="secondary">{what} appears here once you load something.</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Use “Pre-fill from current page” or “Fill in the form” at the top.
          </Text>
        </span>
      }
    />
  </div>
);

const StatCard: React.FC<{ card: any }> = ({ card }) => {
  const colour =
    card.direction === 'down' ? '#FF8080' : card.direction === 'flat' ? '#9AA4AE' : '#7DD87D';
  const arrow = card.direction === 'down' ? '▼' : card.direction === 'flat' ? '—' : '▲';
  return (
    <div style={{ background: '#32383E', borderRadius: 8, padding: 16, color: '#fff', minWidth: 0 }}>
      <div style={{ fontSize: 14 }}>{card.title}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <span style={{ fontSize: 28, fontWeight: 700 }}>{card.value}</span>
        {card.out_of && <span style={{ fontSize: 13, color: '#DDE7EE' }}>out of {card.out_of}</span>}
      </div>
      <div style={{ fontSize: 13, marginTop: 6 }}>
        <span style={{ color: colour }}>{arrow} {card.change_pct}%</span>
        <span style={{ color: '#DDE7EE', marginLeft: 6 }}>vs. {card.vs_label}</span>
      </div>
      {card.change_is_approximate && (
        <div style={{ color: '#E8C468', fontSize: 11, marginTop: 4 }}>
          recomputed from rounded prices
        </div>
      )}
    </div>
  );
};

/** The dark band at the top of the page: heading, two subtitles, five cards. */
export const StatBandPreview: React.FC<{ derived: any }> = ({ derived }) => {
  if (!derived) return <NothingYet what="The dark band, with its five cards," />;
  return (
    <div style={{ background: '#171A1C', borderRadius: 8, padding: 20 }}>
      <div style={{ color: '#fff', fontSize: 22, borderBottom: '2px solid #636B74', paddingBottom: 8 }}>
        {derived.headline_period}
      </div>
      <div style={{ color: '#fff', marginTop: 16, fontWeight: 700 }}>Calyx Carbon Integrity Index</div>
      <div style={{ color: '#DDE7EE', fontSize: 13, marginBottom: 8 }}>
        {derived.integrity.stat_subtitle}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
        {derived.stat_cards.integrity.map((c: any) => <StatCard key={c.title} card={c} />)}
      </div>
      <div style={{ color: '#fff', marginTop: 20, fontWeight: 700 }}>
        Calyx-ClearBlue Carbon Price-Integrity Index
      </div>
      <div style={{ color: '#DDE7EE', fontSize: 13, marginBottom: 8 }}>
        {derived.pricing.stat_subtitle}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
        {derived.stat_cards.pricing.map((c: any) => <StatCard key={c.title} card={c} />)}
      </div>
    </div>
  );
};

export const IntegrityChartPreview: React.FC<{ derived: any }> = ({ derived }) => {
  const [frameKey, setFrameKey] = useState<'quarterly' | 'annual'>('quarterly');
  const frame = derived?.integrity?.[frameKey];

  const option = useMemo(() => {
    if (!frame) return {};
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 48, right: 72, top: 24, bottom: 48 },
      xAxis: {
        type: 'category', boundaryGap: false, data: frame.x_axis,
        axisLabel: { interval: frameKey === 'quarterly' ? 1 : 0 },
      },
      yAxis: { type: 'value', min: 1, max: 10 },
      legend: { data: ['Issuances', 'Retirements'], bottom: 0 },
      series: [
        {
          name: 'Issuances', type: 'line', data: frame.issuance, color: GREEN,
          markLine: provisionalMarkLine(frame.issuance, frame.provisional_tail),
        },
        {
          name: 'Retirements', type: 'line', data: frame.retirement, color: INK,
          markLine: provisionalMarkLine(frame.retirement, frame.provisional_tail),
        },
      ],
    };
  }, [frame, frameKey]);

  if (!derived) return <NothingYet what="The integrity chart" />;

  return (
    <div>
      <Radio.Group
        size="small"
        className="mb-2"
        value={frameKey}
        onChange={(e) => setFrameKey(e.target.value)}
        optionType="button"
        options={[
          { label: 'Quarterly', value: 'quarterly' },
          { label: 'Annual', value: 'annual' },
        ]}
      />
      <ReactECharts option={option} style={{ height: 280 }} notMerge />
      {frame?.provisional_tail && (
        <Text type="secondary" style={{ display: 'block', maxWidth: 720 }}>
          The last segment is dashed because {frame.x_axis[frame.x_axis.length - 1]} has not
          finished as of your as-of date. That happens automatically.
        </Text>
      )}
    </div>
  );
};

export const PricingChartPreview: React.FC<{ derived: any }> = ({ derived }) => {
  const [frameKey, setFrameKey] = useState<'monthly' | 'annual'>('monthly');
  const frame = derived?.pricing?.[frameKey];

  const option = useMemo(() => {
    if (!frame) return {};
    return {
      tooltip: { trigger: 'axis', valueFormatter: priceTooltip },
      grid: { left: 56, right: 72, top: 40, bottom: 48 },
      xAxis: { type: 'category', boundaryGap: false, data: frame.x_axis },
      yAxis: {
        type: 'value',
        name: 'USD',
        nameLocation: 'end',
        nameGap: 18,
        nameTextStyle: { align: 'left' },
        axisLabel: { formatter: priceAxisLabel },
      },
      legend: { data: ['T1', 'T2', 'T3'], bottom: 0 },
      series: [
        { name: 'T1', type: 'line', data: frame.t1, color: PURPLE,
          markLine: provisionalMarkLine(frame.t1, frame.provisional_tail) },
        { name: 'T2', type: 'line', data: frame.t2, color: INK,
          markLine: provisionalMarkLine(frame.t2, frame.provisional_tail) },
        { name: 'T3', type: 'line', data: frame.t3, color: GREY,
          markLine: provisionalMarkLine(frame.t3, frame.provisional_tail) },
      ],
    };
  }, [frame]);

  if (!derived) return <NothingYet what="The price chart" />;

  return (
    <div>
      <Radio.Group
        size="small"
        className="mb-2"
        value={frameKey}
        onChange={(e) => setFrameKey(e.target.value)}
        optionType="button"
        options={[
          { label: 'Monthly', value: 'monthly' },
          { label: 'Annual', value: 'annual' },
        ]}
      />
      <ReactECharts option={option} style={{ height: 280 }} notMerge />
      {frameKey === 'annual' && (
        <Alert
          type="info"
          showIcon
          message="You did not enter these numbers"
          description="The annual series is the mean of the monthly prices in each year, calculated when you save."
        />
      )}
    </div>
  );
};
